/**
 * BankConnectionCard - Flag-aware bank connection component
 * 
 * Shows appropriate UI based on:
 * - AKAHU_ENABLED / AKAHU_OAUTH_ENABLED: NZ bank integration
 * - PLAID_ENABLED / PLAID_LINK_ENABLED: US/International bank integration
 * - Connection status: connected, error, or not connected
 * 
 * ARC Doctrine: Calm language, no urgency, no blame
 * Akahu Compliance: Pre-consent info, disclaimers, disconnect confirmation
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import api from "../../lib/api";
import { toast } from "sonner";
import { 
  Building2, 
  Upload, 
  CheckCircle2, 
  AlertCircle,
  RefreshCw,
  Loader2,
  Clock,
  Unlink,
  ExternalLink,
  Shield,
  Info,
  PenLine
} from "lucide-react";
import { BankConsentPanel, BankConnectionDisclaimer, BankConnectionSuccessBanner } from "./BankConsentPanel";
import { DisconnectBankDialog } from "./DisconnectBankDialog";

// Connection status types
const ConnectionState = {
  LOADING: "loading",
  OAUTH_DISABLED: "oauth_disabled",
  NOT_CONNECTED: "not_connected",
  CONNECTED: "connected",
  ERROR: "error"
};

// Provider types
const Provider = {
  NONE: "none",
  AKAHU: "akahu",
  PLAID: "plaid"
};

/**
 * Main BankConnectionCard component
 */
export function BankConnectionCard({ 
  householdId,
  compact = false,
  showCsvAlternative = true,
  showConsentPanel = true,
  showManualOption = true,
  onCsvImport,
  onConnectionChange,
  onSkipToManual
}) {
  const [state, setState] = useState(ConnectionState.LOADING);
  const [provider, setProvider] = useState(Provider.NONE);
  const [flags, setFlags] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  
  // Check URL for connection success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'akahu' || params.get('connected') === '1') {
      setShowSuccessBanner(true);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Fetch integration flags and connection status
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch flags from health endpoint
      const healthRes = await api.get("/api/health");
      if (!healthRes.ok) throw new Error("Could not fetch integration status");
      const healthData = await healthRes.json();
      const featureFlags = healthData.feature_flags || {};
      setFlags(featureFlags);
      
      // Determine which provider to use (Akahu takes priority for NZ)
      const akakuAvailable = featureFlags.akahu_enabled && featureFlags.akahu_oauth_enabled;
      const plaidAvailable = featureFlags.plaid_enabled && featureFlags.plaid_link_enabled;
      
      // Check Akahu first (NZ priority)
      if (akakuAvailable && householdId) {
        setProvider(Provider.AKAHU);
        try {
          const connRes = await api.akahu.getConnection(householdId);
          if (connRes.ok) {
            const connData = await connRes.json();
            setConnectionStatus(connData);
            
            if (connData.connected) {
              setState(connData.sync_status === "error" || connData.last_sync_error 
                ? ConnectionState.ERROR 
                : ConnectionState.CONNECTED);
            } else {
              setState(ConnectionState.NOT_CONNECTED);
            }
          } else {
            setState(ConnectionState.NOT_CONNECTED);
          }
        } catch {
          setState(ConnectionState.NOT_CONNECTED);
        }
      }
      // Check Plaid if Akahu not available
      else if (plaidAvailable && householdId) {
        setProvider(Provider.PLAID);
        // Check for existing Plaid connections
        try {
          const linkedRes = await api.get(`/api/integrations/households/${householdId}/linked-accounts`);
          if (linkedRes.ok) {
            const linkedData = await linkedRes.json();
            const plaidAccounts = (linkedData.accounts || []).filter(a => a.provider === "plaid");
            if (plaidAccounts.length > 0) {
              setConnectionStatus({ connected: true, accounts: plaidAccounts });
              setState(ConnectionState.CONNECTED);
            } else {
              setState(ConnectionState.NOT_CONNECTED);
            }
          } else {
            setState(ConnectionState.NOT_CONNECTED);
          }
        } catch {
          setState(ConnectionState.NOT_CONNECTED);
        }
      }
      // No provider available - show disabled state
      else {
        setProvider(Provider.NONE);
        setState(ConnectionState.OAUTH_DISABLED);
      }
    } catch (error) {
      console.error("BankConnectionCard fetch error:", error);
      setProvider(Provider.NONE);
      setState(ConnectionState.OAUTH_DISABLED);
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle connect button click
  const handleConnect = async () => {
    if (!householdId) {
      toast.error("No household selected");
      return;
    }
    
    setActionLoading(true);
    try {
      if (provider === Provider.AKAHU) {
        // Akahu OAuth flow
        const res = await api.get(`/api/integrations/akahu/connect-url?household_id=${householdId}`);
        
        if (res.ok) {
          const data = await res.json();
          window.location.href = data.authorization_url;
        } else if (res.status === 503) {
          toast.error("Bank connection is not currently available");
          setState(ConnectionState.OAUTH_DISABLED);
        } else {
          const errorData = await res.json().catch(() => ({}));
          toast.error(errorData.detail || "Could not start bank connection");
        }
      } else if (provider === Provider.PLAID) {
        // Plaid Link flow
        const res = await api.post(`/api/integrations/households/${householdId}/plaid/create-link-token`);
        
        if (res.ok) {
          const data = await res.json();
          // Store link token and open Plaid Link
          // Note: Plaid Link integration would be handled by Plaid SDK
          toast.info("Plaid Link is being prepared. Please check back later.");
          // For now, just show the token was created
          console.log("Plaid link token created:", data.link_token);
        } else if (res.status === 503) {
          toast.error("Bank connection is not currently available");
          setState(ConnectionState.OAUTH_DISABLED);
        } else {
          const errorData = await res.json().catch(() => ({}));
          toast.error(errorData.detail || "Could not start bank connection");
        }
      }
    } catch (error) {
      console.error("Connect error:", error);
      toast.error("Could not connect to bank service");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle disconnect
  const handleDisconnect = async () => {
    if (!householdId) return;
    
    setActionLoading(true);
    try {
      if (provider === Provider.AKAHU) {
        const res = await api.akahu.disconnect(householdId);
        
        if (res.ok) {
          toast.success("Bank disconnected");
          setConnectionStatus(null);
          setState(flags?.akahu_oauth_enabled ? ConnectionState.NOT_CONNECTED : ConnectionState.OAUTH_DISABLED);
          onConnectionChange?.({ connected: false });
        } else {
          toast.error("Could not disconnect bank");
        }
      } else if (provider === Provider.PLAID) {
        // Plaid disconnect - remove linked accounts
        toast.info("Disconnecting Plaid accounts...");
        // Would need to implement DELETE endpoint for Plaid
        setConnectionStatus(null);
        setState(flags?.plaid_link_enabled ? ConnectionState.NOT_CONNECTED : ConnectionState.OAUTH_DISABLED);
        onConnectionChange?.({ connected: false });
      }
    } catch (error) {
      console.error("Disconnect error:", error);
      toast.error("Could not disconnect bank");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle reconnect (for error state)
  const handleReconnect = async () => {
    // First disconnect, then reconnect
    await handleDisconnect();
    // Small delay before reconnect
    setTimeout(() => {
      handleConnect();
    }, 500);
  };

  // Handle refresh/sync
  const handleRefresh = async () => {
    if (!householdId) return;
    
    setActionLoading(true);
    try {
      if (provider === Provider.AKAHU) {
        const res = await api.akahu.syncTransactions(householdId, false);
        
        if (res.ok) {
          toast.success("Transactions synced");
          await fetchData();
          onConnectionChange?.({ synced: true });
        } else {
          toast.error("Could not sync transactions");
        }
      } else if (provider === Provider.PLAID) {
        // Plaid sync
        const linkedAccounts = connectionStatus?.accounts || [];
        if (linkedAccounts.length > 0) {
          const linkedAccountId = linkedAccounts[0].linked_account_id;
          const res = await api.post(`/api/integrations/households/${householdId}/plaid/sync`, {
            linked_account_id: linkedAccountId
          });
          
          if (res.ok) {
            toast.success("Transactions synced");
            await fetchData();
            onConnectionChange?.({ synced: true });
          } else {
            toast.error("Could not sync transactions");
          }
        }
      }
    } catch (error) {
      console.error("Sync error:", error);
      toast.error("Could not sync transactions");
    } finally {
      setActionLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card data-testid="bank-connection-card-loading">
        <CardContent className={compact ? "p-4" : "p-6"}>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // OAuth Disabled State
  if (state === ConnectionState.OAUTH_DISABLED) {
    return (
      <Card data-testid="bank-connection-card-disabled">
        <CardContent className={compact ? "p-4" : "p-6"}>
          <div className="flex items-start gap-4">
            <div className={`${compact ? "w-10 h-10" : "w-12 h-12"} rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0`}>
              <Building2 className={`${compact ? "w-5 h-5" : "w-6 h-6"} text-slate-400`} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={`font-medium text-foreground ${compact ? "text-sm" : ""}`}>
                Bank connection
              </h3>
              <p className={`text-muted-foreground mt-1 ${compact ? "text-xs" : "text-sm"}`}>
                Bank connection will be available soon.
              </p>
              
              {/* CSV Import Alternative */}
              {showCsvAlternative && (
                <div className="mt-4 pt-4 border-t">
                  <p className={`text-muted-foreground mb-3 ${compact ? "text-xs" : "text-sm"}`}>
                    You can import transactions manually in the meantime.
                  </p>
                  <Button
                    variant="outline"
                    size={compact ? "sm" : "default"}
                    onClick={onCsvImport}
                    data-testid="csv-import-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Import CSV
                  </Button>
                </div>
              )}
              
              {/* Manual Mode Option */}
              {showManualOption && onSkipToManual && (
                <div className="mt-5 pt-4 border-t border-border">
                  {/* Divider with "or" */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 h-px bg-border"></div>
                    <span className="text-xs text-muted-foreground font-medium">or</span>
                    <div className="flex-1 h-px bg-border"></div>
                  </div>
                  
                  {/* Manual mode button */}
                  <Button
                    variant="ghost"
                    onClick={onSkipToManual}
                    className="w-full justify-center text-muted-foreground hover:text-foreground"
                    size={compact ? "sm" : "default"}
                    data-testid="continue-manual-btn"
                  >
                    <PenLine className="w-4 h-4 mr-2" />
                    Continue without connecting a bank
                  </Button>
                  
                  {/* Description */}
                  <p className={`text-center text-muted-foreground mt-2 ${compact ? "text-xs" : "text-xs"}`}>
                    You can still use ARA Wealth by manually adding accounts and transactions.
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Not Connected State (OAuth Available)
  if (state === ConnectionState.NOT_CONNECTED) {
    return (
      <div className="space-y-4" data-testid="bank-connection-not-connected-container">
        {/* Pre-consent Panel */}
        {showConsentPanel && !compact && (
          <BankConsentPanel compact={compact} />
        )}
        
        <Card data-testid="bank-connection-card-not-connected">
          <CardContent className={compact ? "p-4" : "p-6"}>
            <div className="flex items-start gap-4">
              <div className={`${compact ? "w-10 h-10" : "w-12 h-12"} rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0`}>
                <Building2 className={`${compact ? "w-5 h-5" : "w-6 h-6"} text-emerald-600 dark:text-emerald-400`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`font-medium text-foreground ${compact ? "text-sm" : ""}`}>
                  Connect your bank
                </h3>
                <p className={`text-muted-foreground mt-1 ${compact ? "text-xs" : "text-sm"}`}>
                  Read-only access. You can disconnect anytime.
                </p>
                
                {/* Compact consent info */}
                {showConsentPanel && compact && (
                  <Link 
                    to="/security/open-banking"
                    className="inline-flex items-center gap-1 text-xs text-[#1a4d3e] hover:underline mt-2"
                  >
                    <Shield className="w-3 h-3" />
                    Learn how we securely connect
                  </Link>
                )}
                
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button
                    onClick={handleConnect}
                    disabled={actionLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    size={compact ? "sm" : "default"}
                    data-testid="connect-bank-btn"
                  >
                    {actionLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Connect Bank
                      </>
                    )}
                  </Button>
                  
                  {showCsvAlternative && (
                    <Button
                      variant="outline"
                      size={compact ? "sm" : "default"}
                      onClick={onCsvImport}
                      data-testid="csv-import-alt-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Import CSV
                    </Button>
                  )}
                </div>
                
                {/* Disclaimer below button */}
                {!compact && (
                  <div className="mt-3">
                    <BankConnectionDisclaimer />
                  </div>
                )}
                
                {/* Manual Mode Option */}
                {showManualOption && onSkipToManual && (
                  <div className="mt-6 pt-5 border-t border-border">
                    {/* Divider with "or" */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex-1 h-px bg-border"></div>
                      <span className="text-xs text-muted-foreground font-medium">or</span>
                      <div className="flex-1 h-px bg-border"></div>
                    </div>
                    
                    {/* Manual mode button */}
                    <Button
                      variant="ghost"
                      onClick={onSkipToManual}
                      className="w-full justify-center text-muted-foreground hover:text-foreground"
                      size={compact ? "sm" : "default"}
                      data-testid="continue-manual-btn"
                    >
                      <PenLine className="w-4 h-4 mr-2" />
                      Continue without connecting a bank
                    </Button>
                    
                    {/* Description */}
                    <p className={`text-center text-muted-foreground mt-2 ${compact ? "text-xs" : "text-xs"}`}>
                      You can still use ARA Wealth by manually adding accounts and transactions.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Connected State
  if (state === ConnectionState.CONNECTED) {
    const lastSynced = connectionStatus?.last_sync_at 
      ? new Date(connectionStatus.last_sync_at).toLocaleString('en-NZ', { 
          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
        })
      : connectionStatus?.last_synced_at
        ? new Date(connectionStatus.last_synced_at).toLocaleString('en-NZ', { 
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
          })
        : null;

    const handleDisconnectClick = () => {
      setShowDisconnectDialog(true);
    };
    
    const handleConfirmDisconnect = async () => {
      setShowDisconnectDialog(false);
      await handleDisconnect();
    };

    return (
      <div className="space-y-4" data-testid="bank-connection-connected-container">
        {/* Success Banner (shown after fresh connection) */}
        {showSuccessBanner && (
          <BankConnectionSuccessBanner 
            onDismiss={() => setShowSuccessBanner(false)} 
          />
        )}
        
        <Card data-testid="bank-connection-card-connected">
          <CardContent className={compact ? "p-4" : "p-6"}>
            <div className="flex items-start gap-4">
              <div className={`${compact ? "w-10 h-10" : "w-12 h-12"} rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0`}>
                <Building2 className={`${compact ? "w-5 h-5" : "w-6 h-6"} text-teal-600 dark:text-teal-400`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className={`font-medium text-foreground ${compact ? "text-sm" : ""}`}>
                    Bank connected
                  </h3>
                  <Badge className="bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/50 dark:text-teal-300 dark:border-teal-700" data-testid="connected-badge">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Connected via Akahu
                  </Badge>
                </div>
                
                {lastSynced && (
                  <p className={`text-muted-foreground mt-1 flex items-center ${compact ? "text-xs" : "text-sm"}`}>
                    <Clock className="w-3 h-3 mr-1" />
                    Last synced: {lastSynced}
                  </p>
                )}
                
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={actionLoading}
                    data-testid="refresh-sync-btn"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    <span className="ml-1">Sync Now</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDisconnectClick}
                    disabled={actionLoading}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid="disconnect-btn"
                  >
                    <Unlink className="w-4 h-4 mr-1" />
                    Disconnect
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Disconnect Confirmation Dialog */}
        <DisconnectBankDialog
          open={showDisconnectDialog}
          onOpenChange={setShowDisconnectDialog}
          onConfirm={handleConfirmDisconnect}
          loading={actionLoading}
        />
      </div>
    );
  }

  // Error State
  if (state === ConnectionState.ERROR) {
    return (
      <Card data-testid="bank-connection-card-error">
        <CardContent className={compact ? "p-4" : "p-6"}>
          <div className="flex items-start gap-4">
            <div className={`${compact ? "w-10 h-10" : "w-12 h-12"} rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0`}>
              <AlertCircle className={`${compact ? "w-5 h-5" : "w-6 h-6"} text-amber-600 dark:text-amber-400`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`font-medium text-foreground ${compact ? "text-sm" : ""}`}>
                  Connection needs attention
                </h3>
                <Badge variant="outline" className="border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400" data-testid="error-badge">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Needs reconnect
                </Badge>
              </div>
              <p className={`text-muted-foreground mt-1 ${compact ? "text-xs" : "text-sm"}`}>
                Your bank connection encountered an issue. Please reconnect when you have a moment.
              </p>
              
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  onClick={handleReconnect}
                  disabled={actionLoading}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  size={compact ? "sm" : "default"}
                  data-testid="reconnect-btn"
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Reconnecting...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reconnect
                    </>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size={compact ? "sm" : "default"}
                  onClick={handleDisconnect}
                  disabled={actionLoading}
                  className="text-muted-foreground"
                  data-testid="disconnect-error-btn"
                >
                  <Unlink className="w-4 h-4 mr-1" />
                  Disconnect instead
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fallback
  return null;
}

export default BankConnectionCard;
